import { useCallback, useEffect, useMemo, useState } from 'react'
import { Globe, Loader2 } from 'lucide-react'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportOnlineBookingFunnelReport,
  fetchOnlineBookingFunnelReport,
  formatReportDate,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/15 px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tracking-tight">{value}</p>
    </div>
  )
}

export function OnlineBookingFunnelReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
    }),
    [period, customFrom, customTo, bounds, compare],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchOnlineBookingFunnelReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load online booking funnel report.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportOnlineBookingFunnelReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  const hasActivity = (report?.summary?.submitted_count ?? 0) > 0
  const maxFunnelCount = Math.max(...(report?.funnel ?? []).map((step) => step.count), 1)

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Globe className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Online booking funnel</h2>
            <p className="text-sm text-muted-foreground">
              Guest submissions through confirmation, visit, completion, and sale conversion.
            </p>
          </div>
        </div>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onExportCsv={() => handleExport('csv')}
          onExportPdf={() => handleExport('pdf')}
          exporting={exporting}
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <SummaryCard label="Submitted" value={String(report.summary.submitted_count)} />
            <SummaryCard label="Confirmed" value={String(report.summary.confirmed_count)} />
            <SummaryCard label="Checked in" value={String(report.summary.checked_in_count)} />
            <SummaryCard label="Completed" value={String(report.summary.completed_count)} />
            <SummaryCard
              label="Sale conversion"
              value={
                report.summary.sale_rate_pct != null ? `${report.summary.sale_rate_pct}%` : '—'
              }
            />
          </div>

          {report.compare_summary ? (
            <p className="mb-4 text-sm text-muted-foreground">
              Compare period: {report.compare_summary.submitted_count} submitted ·{' '}
              {report.compare_summary.confirmation_rate_pct ?? '—'}% confirmed
            </p>
          ) : null}

          <div className="mb-6 space-y-3 rounded-2xl border border-border/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Conversion funnel
            </p>
            {!hasActivity ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No online bookings submitted in this period.
              </p>
            ) : (
              report.funnel.map((step) => (
                <div key={step.key}>
                  <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium">{step.label}</span>
                    <span className="text-muted-foreground">
                      {step.count}
                      {step.conversion_pct != null ? ` · ${step.conversion_pct}%` : ''}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted/50">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${Math.max(4, (step.count / maxFunnelCount) * 100)}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(report.outcomes ?? []).map((item) => (
              <div key={item.key} className="rounded-2xl border border-border/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{item.label}</p>
                <p className="mt-1 text-xl font-semibold">{item.count}</p>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto rounded-2xl border border-border/70">
            <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Daily submissions
            </p>
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium">Confirmed</th>
                  <th className="px-4 py-3 font-medium">Completed</th>
                  <th className="px-4 py-3 font-medium">Sold</th>
                  <th className="px-4 py-3 font-medium">Cancelled</th>
                </tr>
              </thead>
              <tbody>
                {(report.by_day ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No daily data for this period.
                    </td>
                  </tr>
                ) : (
                  report.by_day.map((row) => (
                    <tr key={row.date} className="border-t border-border/50">
                      <td className="px-4 py-3 font-medium">{formatReportDate(row.date)}</td>
                      <td className="px-4 py-3">{row.submitted_count}</td>
                      <td className="px-4 py-3">{row.confirmed_count}</td>
                      <td className="px-4 py-3">{row.completed_count}</td>
                      <td className="px-4 py-3">{row.sold_count}</td>
                      <td className="px-4 py-3">{row.cancelled_count}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  )
}
